import { useState } from "react";
import { FaFileExcel } from "react-icons/fa6";
import { Card, CardBody, CardTitle, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { IValueListAdminQuery, IValueListAdminQueryParameter } from "../../../types/valuelist";
import AccordionContainer from "../../common/accordion-container";
import BusyButton from "../../common/busy-button";
import useAuthToken from "../../../hook/useAuthToken";
import moment from "moment";
import AutoFocusDiv from "../../common/auto-focus-div";
import InlineFeedback from "../../common/inline-feedback";
import SpinnerOverlay from "../../common/spinner/spinner";
import { notifyError } from "../../../services/notification";
import Info from "../../common/info";

interface IQueryParameterValue extends IValueListAdminQueryParameter {
  value: any | undefined;
  error: string;
}

const QueryScriptPane = (props: { data?: IValueListAdminQuery }) => {
  const { data } = props;

  const [queryParameterValues, setQueryParameterValues] = useState<IQueryParameterValue[]>(initialSetup);
  const [getAccessToken] = useAuthToken();
  const [loading, setLoading] = useState<boolean>(false);

  function initialSetup() : IQueryParameterValue[] {
    if(!data || data.parameters.length == 0) return [];

    return data.parameters.map((p) => ({
      name: p.name,
      type: p.type,
      maxLength: p.maxLength,
      defaultValue: p.defaultValue,
      value: p.type === "int" && p.defaultValue ? parseInt(p.defaultValue) : p.defaultValue ?? "", // init to the default value
      error: ""
    }));
  }

  const handleSubmit = async () => {
    // validate parameters, if any
    if (queryParameterValues.length > 0) {
      // parameters exist, make sure they are valid
      const formElement = document.getElementById('admin-query-parameters');
      if (!formElement || !(formElement instanceof HTMLFormElement)) {
        // form should exist, show error toast
        return;
      }

      const form = formElement as HTMLFormElement;

      // if there are any errors, prevent calling the api
      if (!form.checkValidity() || queryParameterValues.filter(q => q.error !== "").length > 0) {
        // if a field does NOT have a default value, and the user has just clicked the Generate Report button, the form will be invalid
        // but NOT show any validation messages.
        // make sure to show them
        const existingParameterValues = [...queryParameterValues]
        for(let i = 0; i < existingParameterValues.length; i++) {
          const element = document.getElementById(cleanParamName(existingParameterValues[i]));
          if(!element || !(element instanceof HTMLInputElement)) continue; // shouldn't happen since we dynamically generate the form fields
          const inputElement = element as HTMLInputElement;
          existingParameterValues[i].error = getSpecificErrorMessage(inputElement);
        }
        setQueryParameterValues(existingParameterValues);
        return;
      }
    }
    
    const token = await getAccessToken();
    const invalidChars = /[\/\\:\*\?"<>|]/g;
    // clean the file name of invalid chars
    const fileName = data?.label.replace(invalidChars, '').replace(/ /g, '-');

    const payload = queryParameterValues.length == 0 
      ? []
      : queryParameterValues.map((q) => ({
          name: q.name,
          value: q.value
      }));

    if(!token) return;

    // validation passed, show spinner / make API call
    setLoading(true);

    fetch(`/api/data-extract/query/${data?.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload),
      credentials: 'include'
    }).then(response => {
      setLoading(false);
      if (!response.ok) {
        if (response.status === 404) {
          notifyError('The report you are attempting to run is no longer available. Please refresh the page.');
        }
        if (response.status === 400) {
          notifyError('The report you are attempting to run has one or more invalid parameters. Please verify the parameter values, or refresh the page to get the latest version.');
        }
        else {
          notifyError('There was an unexpected error when attempting to run your report, please try again. If this issue persists, please contact the System Administrator.')
        }
        
        return;
      }
      return response.blob();
    }).then(blob => {
        if(!blob) return;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${fileName}-${moment(new Date()).format('YYYYMMDDHHmmss')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        notifyError(`There was a problem generating your report: ${error}`);
        setLoading(false);
      });
  }

  function cleanParamName(queryParameter: IQueryParameterValue) : string {
    return queryParameter.name.replace('@', '');
  }

  function getErrorMessage(queryParameter: IQueryParameterValue) : string {
    return queryParameterValues.find((p => p.name === queryParameter.name))?.error ?? "";
  }

  function getMinValue(queryParameter: IQueryParameterValue) : number {    
    // NOTE: these are the params in use as of 2024-09-22
    switch (queryParameter.name.toLowerCase()) {
      case "@orgId":
        return 0;// org ids must be positive integers
      case "@year":
        return 2000; // 2000+
      case "@month":
        return 1; // 1-12
      default:
        return 0;
    }
  }

  function getMaxValue(queryParameter: IQueryParameterValue) : number {
    // NOTE: these are the params in use as of 2024-09-22
    switch (queryParameter.name.toLowerCase()) {
      case "@orgId":
        return 2147483647; // int maxvalue
      case "@year":
        return new Date().getFullYear(); // cannot be in the future
      case "@month":
        return 12; // 1-12
      default:
        return 2147483647; // int maxvalue
    }
  }

  function setParamValue(queryParameter: IQueryParameterValue, event: React.ChangeEvent<HTMLInputElement>) : void {
    const  existingValues = [...queryParameterValues];
      for (let i = 0; i < existingValues.length; i++) {
        if (existingValues[i].name == queryParameter.name) {
          if (queryParameter.type.toLowerCase() === "int") {
            existingValues[i].value = parseInt(event.target.value);
          }
          else {
            existingValues[i].value = event.target.value;
          }
          if(event.target.validity.valid) {
            existingValues[i].error = "";
          }
          else {
            existingValues[i].error = getSpecificErrorMessage(event.target);
          }
          break;
        }
      }

      setQueryParameterValues(existingValues);
  }

  function getSpecificErrorMessage(element: HTMLInputElement) : string {
    if(element.validity.valueMissing) return "This parameter is required";
    if(element.validity.rangeOverflow) return `This parameter has a maximum value of ${element.max}`;
    if(element.validity.rangeUnderflow) return `This parameter has a minimum value of ${element.min}`;
    if(element.validity.tooLong) return `This parameter has a maximum length of ${element.maxLength}`;
    return "";
  }

  return (
    
    <Card>
      <CardBody>
        <CardTitle className="mb-0">
          <AutoFocusDiv trigger={data}>
            Query: {data?.label}
          </AutoFocusDiv>
        </CardTitle>

        <div>
          <BusyButton type="submit" aria-label="Generate Excel Report" color="primary"
            value="Generate Excel Report" busy={loading}
            onClick={handleSubmit}>
            <FaFileExcel />
          </BusyButton>
        </div>
          { loading && <SpinnerOverlay /> }
          { queryParameterValues.length > 0 && 
            <AccordionContainer title="Parameters">
              <Info>
              Parameters section is now expanded. Please fill out all required fields below to generate your report.
              </Info>
              {queryParameterValues.some(q => q.error) && (
              <Info color="danger">
                Validation Error: Required values are missing for parameters: {
                  queryParameterValues
                    .filter(q => q.error !== "")
                    .map(q => cleanParamName(q))
                    .join(", ")
                }
              </Info>
            )}
              <Form id="admin-query-parameters">
              { queryParameterValues.map((queryParameter) => {
                  return (
                    
                      <FormGroup row key={queryParameter.name}>
                        <Col className="col-md-1">
                          <Label for={cleanParamName(queryParameter)} className="col-auto col-form-label">{cleanParamName(queryParameter)}:</Label>
                        </Col>
                        <Col className="col-md-3">
                          { queryParameter.type === "int" && 
                            <Input type="number" 
                              id={cleanParamName(queryParameter)} 
                              step={1} 
                              max={getMaxValue(queryParameter)}
                              min={getMinValue(queryParameter)} 
                              onChange={e => setParamValue(queryParameter, e)}                    
                              defaultValue={queryParameter.defaultValue ?? ""}
                              invalid={!!getErrorMessage(queryParameter)}
                              required
                              /> 
                          }
                          { (queryParameter.type === "nvarchar" || queryParameter.type === "varchar") && 
                            <Input type="text" 
                              id={cleanParamName(queryParameter)} 
                              defaultValue={queryParameter.defaultValue ?? ""} 
                              maxLength={queryParameter.maxLength} 
                              onChange={e => setParamValue(queryParameter, e)}
                              invalid={!!getErrorMessage(queryParameter)}
                              required  /> }
                        </Col>
                        <Row>
                          <Col className="col-md-3 offset-md-1">
                            <InlineFeedback invalid={!!getErrorMessage(queryParameter)}>{getErrorMessage(queryParameter)}</InlineFeedback>
                          </Col>
                        </Row>
                      </FormGroup>
                    
                  )
              })}
              </Form>
            </AccordionContainer>
          }
      </CardBody>
    </Card>
  );
};

export default QueryScriptPane;
